import { useEffect, useState } from 'react';
import { Button, Col, Flex, Image, Modal, Row, Skeleton, Tabs, TabsProps } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

// services
import NotificationService from 'core/services/notification';
import { onChargePointWallet, onGetUser, onGetUserBalance } from 'modules/users/services/users';

// components
import BackButton from 'components/backButton/backButton';
import Icon from 'utils/icon';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ShareTransactionsComponent from '../../components/share-transactions/share-transactions';
import InvestmentsComponent from 'modules/users/components/investments/investments';
import CompaniesComponent from 'modules/users/components/companies/companies';
import WalletTransactionsComponent from 'modules/users/components/wallet-transactions/wallet-transactions';
import BankAccountsComponent from 'modules/users/components/bank-accounts/bank-accounts';
import PaymentsComponent from 'modules/users/components/payments/payments';
import WithdrawRequestsComponent from 'modules/users/components/withdraw-requests/withdraw-requests';
import PointWalletTransactionsComponent from 'modules/users/components/point-wallet-transactions/point-wallet-transactions';

// models
import { User } from 'modules/users/models/user';
import { WalletBalance } from 'modules/users/models/wallet-balance';

//styles
import styles from './view.module.scss';
import { isRTL } from 'core/services/language';
import { useTranslation } from 'react-i18next';

const AMOUNTS = [35, 299, 575, 1099, 4599];

function UserViewPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [user, setUser] = useState<User>();
	const [userBalance, setUserBalance] = useState<WalletBalance>();

	const [isChargePointsModalOpen, setIsChargePointsModalOpen] = useState<boolean>(false);
	const [selectedAmount, setSelectedAmount] = useState<(typeof AMOUNTS)[number]>(35);
	const [isChargingPoints, setIsChargingPoints] = useState<boolean>(false);

	const userTabItems: TabsProps['items'] = [
		{
			key: '1',
			label: t('USERS.SHARE_TRANSACTIONS'),
			destroyInactiveTabPane: true,
			children: <ShareTransactionsComponent id={Number(id)} />,
		},
		{
			key: '2',
			label: t('USERS.INVESTMENTS'),
			destroyInactiveTabPane: true,
			children: <InvestmentsComponent id={Number(id)} />,
		},
		{
			key: '3',
			label: t('USERS.COMPANIES'),
			destroyInactiveTabPane: true,
			children: <CompaniesComponent id={Number(id)} />,
		},
		{
			key: '4',
			label: t('USERS.WALLET_TRANSACTIONS'),
			destroyInactiveTabPane: true,
			children: <WalletTransactionsComponent id={Number(id)} />,
		},
		{
			key: '5',
			label: t('USERS.BANK_ACCOUNTS'),
			destroyInactiveTabPane: true,
			children: <BankAccountsComponent id={Number(id)} />,
		},
		{
			key: '6',
			label: t('USERS.PAYMENTS'),
			destroyInactiveTabPane: true,
			children: <PaymentsComponent id={Number(id)} />,
		},
		{
			key: '7',
			label: t('USERS.WITHDRAW_REQUESTS'),
			destroyInactiveTabPane: true,
			children: <WithdrawRequestsComponent id={Number(id)} />,
		},
		{
			key: '8',
			label: t('USERS.POINT_WALLET_TRANSACTIONS'),
			destroyInactiveTabPane: true,
			children: <PointWalletTransactionsComponent id={Number(id)} />,
		},
	];

	useEffect(() => {
		getUser();
	}, []);

	const getUser = async () => {
		try {
			const response = await onGetUser(id as string);

			setUser(response.data.data);

			getUserBalance();

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			navigate('/users');
		}
	};

	const getUserBalance = async () => {
		try {
			const response = await onGetUserBalance(id as string);

			setUserBalance(response.data.data);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const closeChargingModalHandler = () => {
		setIsChargePointsModalOpen(false);
		setSelectedAmount(35);
	};

	const chargePointsHandler = async () => {
		try {
			setIsChargingPoints(true);

			await onChargePointWallet(Number(id), selectedAmount);

			NotificationService.success(t('COMMON.SUCCESS'), t('USERS.USER_WALLET_SUCCESS'));

			setSelectedAmount(35);
			setIsChargePointsModalOpen(false);
			setIsChargingPoints(false);

		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('USERS.ERROR_OCCURRED_CHARGE'));
			setIsChargingPoints(false);
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('USERS.CLIENT_DETAILS')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<div className={`sectionContainer`}>
						<div className={styles.data}>
							<div className={styles.data_logo} style={{ alignItems: isRTL() ? 'start' : 'center' }}>
								{user?.avatar_preview ? (
									<img src={user?.avatar_preview} />
								) : isRTL() ? (
									user?.name?.ar?.slice(0, 1)
								) : (
									user?.name?.en?.slice(0, 1)
								)}
							</div>
							<div>
								<div>
									<div className={styles.data_name}>
										<h5>{isRTL() ? user?.name?.ar : user?.name?.en}</h5>
										{user?.is_phone_verified && <Icon name="verified" />}

										<span className={`status ${user?.profile_status == 'Active' ? 'active' : 'danger'}`}>{user?.profile_status}</span>
									</div>

									<div className={styles.data_connection}>
										<Row gutter={[10, 10]}>
											<Col span={12}>
												<Icon name="contact" />
												<span style={{ direction: 'ltr' }}>{`+${user?.phone_country_code} ${user?.phone_number}`}</span>
											</Col>
											<Col span={12}>
												<Icon name="id" />
												{user?.national_id}
											</Col>
											<Col span={12}>
												<Icon name="location" />
												{user?.country?.name}
											</Col>
											<Col span={12}>
												<Icon name="country2" />
												{user?.address}
											</Col>
											<Col span={12}>
												<Icon name="heart" />
												{user?.date_of_birth ? dayjs(user?.date_of_birth).format('YYYY-MM-DD') : ''}
											</Col>
											{user?.email && (
												<Col span={12}>
													<Icon name="mail" />
													{user?.email}
												</Col>
											)}
										</Row>
									</div>

									<div className={styles.data_details}>
										{user?.default_user_signature_url && (
											<div>
												<span>{t('USERS.SIGNATURE')}</span>
												<Image src={user?.default_user_signature_url} />
											</div>
										)}

										<div>
											<span>{t('USERS.READ_LAST_AGREEMENT')}</span>
											<p>{user?.read_latest_agreement_terms}</p>
										</div>

										<div>
											<span>{t('USERS.AVAILABLE_BUYING_POWER')}</span>
											<p>
												{userBalance && userBalance.available_buying_power ? (
													<p className="priceTag">
														{userBalance.available_buying_power}
														<Icon name="riyal" width={13} height={13} />
													</p>
												) : (
													<Skeleton.Input active={true} size="small" />
												)}
											</p>
										</div>

										<div>
											<span>{t('USERS.AVAILABLE_WITHDRAW')}</span>
											<p>
												{userBalance && userBalance.available_withdraw_able ? (
													<p className="priceTag">
														{userBalance.available_withdraw_able}
														<Icon name="riyal" width={13} height={13} />
													</p>
												) : (
													<Skeleton.Input active={true} size="small" />
												)}
											</p>
										</div>
									</div>
								</div>
								<div className="d-flex flex-wrap gap-2">
									<Button className="discard" style={{ gap: 4 }} onClick={() => setIsChargePointsModalOpen(true)}>
										<Icon name="energy" color="#57dc57" width={16} height={16} />
										{t('USERS.CHARGE_POINTS')}
									</Button>

									<Link to={`/users/edit/${id}`}>
										<Button className="discard" style={{ gap: 4 }}>
											<Icon name="pen" width={14} height={14} />
											{t('COMMON.EDIT')}
										</Button>
									</Link>
								</div>
							</div>
						</div>
					</div>

					<div className="sectionContainer">
						<Tabs
							defaultActiveKey="1"
							items={userTabItems}
							size="large"
							animated={{ inkBar: true, tabPane: true }}
							direction={isRTL() ? 'rtl' : 'ltr'}
						/>
					</div>

					<Modal
						open={isChargePointsModalOpen}
						title={t('USERS.CHARGE_POINTS_FOR_USER')}
						onClose={closeChargingModalHandler}
						onCancel={closeChargingModalHandler}
						footer={[
							<Button key="close" danger size="middle" type="link" onClick={closeChargingModalHandler}>
								{t('COMMON.CANCEL')}
							</Button>,
							<Button
								key="charge"
								size="small"
								type="primary"
								onClick={chargePointsHandler}
								style={{ padding: '8px 15px' }}
								loading={isChargingPoints}>
								{t('USERS.CHARGE')}
							</Button>,
						]}>
						<div className="py-3">
							<h6 className='mb-3'>{t('USERS.SELECT_CHARGE_POINTS_AMOUNT')}:</h6>
							<Flex gap={10}>
								{AMOUNTS.map((amount) => (
									<Button
										style={{ padding: '4px 20px' }}
										key={amount}
										type={selectedAmount === amount ? 'primary' : 'default'}
										onClick={() => setSelectedAmount(amount)}>
										{amount}
									</Button>
								))}
							</Flex>
						</div>
					</Modal>
				</>
			)}
		</>
	);
}

export default UserViewPage;
